import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CanalConectado, Conversacion, NivelSaldo } from '../../api/ops/buzon';
import { Campo } from '../../componentes/Campo';
import { Franja } from '../../componentes/Franja';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, Tarjeta } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { useCabecera, type TarjetaAyuda } from '../../marco/cabecera';
import { estadoDeError, type EstadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { AjustesDelBuzon } from './Ajustes';
import { Hilo } from './Hilo';
import {
    busquedaValida,
    canal,
    consultaDe,
    franjaDeSaldo,
    esperaPersona,
    filtrosDe,
    horaDeLista,
    ICONO_AJUSTES,
    iniciales,
    nombreDe,
    opcionesCanal,
    opcionesMostrar,
    prefijoDelExtracto,
    RUTA_MENSAJES_PLAN,
    vacioDe,
    type Asignadas,
    type ClaveFiltro,
    type Contadores,
    type Quien,
} from './reglas';

/**
 * Conversaciones del Buzón (11.3): una tarjeta con la lista (360, o 300 si no cabe) y la conversación abierta; en móvil,
 * la lista a pantalla completa y la conversación encima con «‹». `?conversacion=` abre una (desde la campana, destino
 * `conversacion`); `?filtro=` elige el filtro.
 *
 * La búsqueda (`q`, de 3 a 100 letras; con menos no se busca) y los contadores «Sin leer», «Asignadas a mí» y «Sin
 * asignar» los da el servidor (`counts`, G728): el panel no cuenta. Arriba, la franja del saldo de WhatsApp con el nivel
 * de la lista (`whatsapp_balance_level`, G726). Lo que la API no da y por eso no se pinta: la vista previa del último
 * mensaje en la lista, cuánto lleva esperando y el número de los demás filtros.
 */
const ESPERA_BUSQUEDA_MS = 300;
const AYUDA: TarjetaAyuda[] = [
    { icono: 'inbox', titulo: 'Buzón', texto: 'Las conversaciones de todos tus canales: WhatsApp, Instagram, Telegram, el chat de la web y las respuestas a tus emails, con todo el historial.' },
    { icono: 'headset', titulo: 'Tomar y responder', texto: 'Cuando el asistente pasa una conversación a una persona, te avisa la campana. Al tomarla queda asignada a ti y el asistente deja de contestar.' },
    // G781: lo que cuenta «Sin asignar» (state human sin assigned_member_id), igual que counts.unassigned y assigned=none.
    { icono: 'circle', titulo: 'Sin asignar', texto: 'Las que esperan a una persona y nadie ha cogido todavía. Las que lleva el asistente y las cerradas no cuentan.' },
    { icono: 'clock', titulo: 'Ventana de 24 h', texto: 'WhatsApp e Instagram dejan escribir libremente durante 24 h desde el último mensaje del cliente. Después, solo con una plantilla aprobada por Meta. Cada mensaje por WhatsApp, también las respuestas, descuenta uno de tus mensajes.' },
    { icono: 'sparkles', titulo: 'Devolver al asistente', texto: 'Cuando acabes, devuélvesela con un resumen. Si nadie escribe en 24 h vuelve sola al asistente, salvo que la fijes.' },
];
const AYUDA_RECEPCION: TarjetaAyuda[] = [
    { icono: 'inbox', titulo: 'Tus conversaciones', texto: 'Ves las que tienes asignadas y las que esperan a una persona. Las que atiende otra persona no aparecen.' },
    { icono: 'headset', titulo: 'Tomar y responder', texto: 'Al tomar una que espera, queda asignada a ti y el asistente deja de contestar.' },
    { icono: 'clock', titulo: 'Ventana de 24 h', texto: 'WhatsApp e Instagram dejan escribir libremente durante 24 h desde el último mensaje del cliente. Después, solo con una plantilla aprobada por Meta.' },
];

const AYUDA_SIN_ASISTENTE: TarjetaAyuda[] = [
    { icono: 'inbox', titulo: 'Buzón sin asistente', texto: 'Sin el bloque Asistente IA, todo lo que escriben tus clientes llega directamente aquí para que lo atienda una persona.' },
    { icono: 'clock', titulo: 'Ventana de 24 h', texto: 'WhatsApp e Instagram dejan escribir libremente durante 24 h desde el último mensaje del cliente. Después, solo con una plantilla aprobada por Meta.' },
];

const DE_URL: Readonly<Record<string, ClaveFiltro>> = { todas: 'todas', 'sin-leer': 'sin-leer', persona: 'persona', asistente: 'asistente', cerradas: 'cerradas' };

interface Lista {
    clave: string;
    items: Conversacion[];
    siguiente: string | null;
    error: EstadoDeError | null;
    cuentas: Contadores | null;
    saldo: NivelSaldo | null;
}

export function Conversaciones(props: PropsPantalla & { quien: Quien; ahora?: () => Date }) {
    const { ops, negocio, movil, busqueda, ruta, navegar, soloLectura, quien, membresia } = props;
    const ahora = useMemo(() => (props.ahora ?? (() => new Date()))(), [props.ahora]);
    const zona = membresia?.timezone;
    const { puede } = usePermisos();
    const filtros = filtrosDe(quien);
    const pedido = DE_URL[busqueda.get('filtro') ?? ''] ?? 'todas';
    const [elegido, setFiltro] = useState<ClaveFiltro>(pedido);
    const filtro: ClaveFiltro = filtros.some(([c]) => c === elegido) ? elegido : 'todas';
    const [canalId, setCanalId] = useState('');
    const [asignadas, setAsignadas] = useState<Asignadas>('any');
    const [texto, setTexto] = useState('');
    const [busca, setBusca] = useState<string | null>(null);
    const [canales, setCanales] = useState<CanalConectado[] | null>(null);
    const [lista, setLista] = useState<Lista | null>(null);
    const [mas, setMas] = useState<{ cursor: string; ocupado: boolean } | null>(null);
    const [intento, setIntento] = useState(0);
    const [nombres, setNombres] = useState<ReadonlyMap<string, string>>(new Map());
    // «Cambiar en el Buzón» del Asistente (11.5, G689) llega con `?panel=ajustes`: abre Ajustes del buzón.
    const [ajustes, setAjustes] = useState(busqueda.get('panel') === 'ajustes');
    const elegida = busqueda.get('conversacion');

    useCabecera({
        ayuda: quien.suyas ? AYUDA_RECEPCION : quien.asistente ? AYUDA : AYUDA_SIN_ASISTENTE,
        acciones: quien.configura ? [{ clave: 'ajustes-buzon', texto: movil ? 'Ajustes' : 'Ajustes del buzón', icono: ICONO_AJUSTES, secundaria: true, onClick: () => { setAjustes(true); } }] : [],
    });

    // Canales del negocio: el estado «sin canales» y el filtro por canal.
    useEffect(() => {
        let vivo = true;
        ops.listChannels(negocio)
            .then((l) => { if (vivo) setCanales(l.data); })
            .catch(() => { if (vivo) setCanales([]); });
        return () => { vivo = false; };
    }, [ops, negocio]);

    // Nombres del equipo (asignada a…, quién respondió). Sin permiso para verlos, «otra persona del equipo».
    useEffect(() => {
        let vivo = true;
        ops.listMembers(negocio, { limit: 100 })
            .then((l) => { if (vivo) setNombres(new Map(l.data.map((m) => [m.id, m.display_name]))); })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, negocio]);

    // La búsqueda sale al dejar de escribir y solo con 3 letras o más (G728).
    useEffect(() => {
        const reloj = setTimeout(() => {
            setBusca(busquedaValida(texto));
            setMas(null);
        }, ESPERA_BUSQUEDA_MS);
        return () => { clearTimeout(reloj); };
    }, [texto]);

    const consulta = consultaDe(filtro, canalId === '' ? null : canalId, asignadas, busca);
    const clave = `${JSON.stringify(consulta)}|${String(intento)}`;
    useEffect(() => {
        let vivo = true;
        ops.listConversations(negocio, { ...consulta, limit: 30 })
            .then((l) => { if (vivo) setLista({ clave, items: l.data, siguiente: l.next_cursor, error: null, cuentas: l.counts, saldo: l.whatsapp_balance_level ?? null }); })
            .catch((e: unknown) => { if (vivo) setLista({ clave, items: [], siguiente: null, error: estadoDeError(e), cuentas: null, saldo: null }); });
        return () => { vivo = false; };
        // `consulta` sale de `clave`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, clave]);

    const actual = lista?.clave === clave ? lista : null;
    const visibles = useMemo(() => (actual?.items ?? []).filter((c) => filtro !== 'todas' || c.state !== 'closed'), [actual, filtro]);

    const verMas = () => {
        const cursor = actual?.siguiente ?? null;
        if (cursor === null || actual === null) {
            return;
        }
        setMas({ cursor, ocupado: true });
        ops.listConversations(negocio, { ...consulta, cursor, limit: 30 })
            .then((l) => {
                setLista((x) => (x === null || x.clave !== clave ? x : { ...x, items: [...x.items, ...l.data.filter((n) => !x.items.some((v) => v.id === n.id))], siguiente: l.next_cursor }));
                setMas(null);
            })
            .catch(() => { setMas(null); });
    };

    const base = ruta.split('?')[0] ?? ruta;
    const abrir = useCallback(
        (id: string | null) => {
            const q = new URLSearchParams(busqueda);
            if (id === null) {
                q.delete('conversacion');
            } else {
                q.set('conversacion', id);
            }
            const texto = q.toString();
            navegar(texto === '' ? base : `${base}?${texto}`, true);
        },
        [busqueda, navegar, base],
    );

    // En ordenador se abre sola la primera que espera a una persona; si no hay, la más reciente.
    const abierta = elegida ?? (movil ? null : (visibles.find((c) => esperaPersona(c)) ?? visibles[0])?.id ?? null);

    const alCambiar = useCallback((c: Conversacion) => {
        setLista((x) => (x === null ? x : { ...x, items: x.items.map((v) => (v.id === c.id ? c : v)) }));
    }, []);

    const sinCanales = canales !== null && canales.filter((c) => c.status !== 'disconnected').length === 0;
    const conFiltros = canalId !== '' || asignadas !== 'any' || busca !== null;
    const saldo = franjaDeSaldo(lista?.saldo, puede('plan.ver'));

    let cuerpo: React.ReactNode;
    if (canales === null || actual === null) {
        cuerpo = <EstadoCargando seccion="conversaciones" />;
    } else if (actual.error !== null) {
        cuerpo = (
            <Tarjeta
                icono="triangle-alert"
                baldosa="var(--rv-red-soft)"
                tinta="var(--rv-red)"
                ceja="Error"
                cejaColor="var(--rv-red)"
                titulo="No he podido cargar las conversaciones"
                texto="Puede ser la conexión. Los mensajes de tus clientes no se pierden: siguen llegando. Prueba de nuevo."
                botones={<Boton icono="refresh-cw" gesto="gira" movil={movil} onClick={() => { setIntento((i) => i + 1); }}>Reintentar</Boton>}
                codigo={actual.error.codigoSoporte}
            />
        );
    } else if (sinCanales && actual.items.length === 0) {
        const admin = puede('canales.canales.gestionar');
        cuerpo = (
            <Tarjeta
                icono="inbox"
                baldosa="var(--rv-paper)"
                tinta="var(--rv-ink)"
                ceja="Buzón"
                cejaColor="var(--rv-fg-3)"
                titulo="Todavía no hay ningún canal conectado"
                texto={`Conecta WhatsApp, Instagram, Telegram o el chat de la web y aquí verás lo que te escriben tus clientes.${admin ? '' : ' Pídeselo al propietario o a un administrador.'}`}
                botones={admin ? <Boton icono="plug" movil={movil} onClick={() => { navegar('/canales'); }}>Conectar un canal</Boton> : undefined}
            />
        );
    } else if (actual.items.length === 0 && filtro === 'todas' && !conFiltros) {
        cuerpo = (
            <Tarjeta
                icono="inbox"
                baldosa="var(--rv-paper)"
                tinta="var(--rv-ink)"
                ceja="Buzón"
                cejaColor="var(--rv-fg-3)"
                titulo="Todavía no hay conversaciones"
                texto="Cuando un cliente escriba por tus canales o responda a un email, la conversación aparecerá aquí."
            />
        );
    } else {
        const vacio = vacioDe(filtro, conFiltros, quien);
        const enHilo = movil && abierta !== null;
        // G1215: «Tú:» solo si el último mensaje lo escribió quien mira (G1226: por su id; sin id de autor, por su nombre).
        const miNombre = quien.yo === null ? null : (nombres.get(quien.yo) ?? null);
        cuerpo = (
            <div className={`rv-bz-split${movil ? ' rv-bz-split--movil' : ''}`}>
                {!enHilo && (
                    <div className="rv-bz-lista" aria-label="Conversaciones">
                        {visibles.length === 0 ? (
                            <div className="rv-bz-vacio"><strong>{vacio.titulo}</strong><span>{vacio.texto}</span></div>
                        ) : (
                            visibles.map((c) => <Fila key={c.id} c={c} activa={c.id === abierta && !movil} ahora={ahora} zona={zona} miNombre={miNombre} miId={quien.yo} onAbrir={() => { abrir(c.id); }} />)
                        )}
                        {actual.siguiente != null && (
                            <div className="rv-bz-mas">
                                <Boton tipo="secundario" icono="chevron-down" movil={movil} ocupado={mas?.ocupado === true} ocupadoTexto="Cargando…" onClick={verMas}>Ver anteriores</Boton>
                            </div>
                        )}
                    </div>
                )}
                {abierta === null ? (
                    !movil && (
                        <div className="rv-bz-hilo rv-bz-hilo--nada">
                            <Icono nombre="message-circle" />
                            <strong>Elige una conversación</strong>
                            <span>Verás aquí todo lo hablado, también con el asistente.</span>
                        </div>
                    )
                ) : (
                    <Hilo
                        key={abierta}
                        id={abierta}
                        resumen={actual.items.find((c) => c.id === abierta) ?? null}
                        ops={ops}
                        negocio={negocio}
                        movil={movil}
                        soloLectura={soloLectura}
                        quien={quien}
                        nombres={nombres}
                        ahora={ahora}
                        zona={zona}
                        navegar={navegar}
                        onVolver={() => { abrir(null); }}
                        onCambio={alCambiar}
                    />
                )}
            </div>
        );
    }

    const filtroIndice = Math.max(0, filtros.findIndex(([c]) => c === filtro));
    // La barra se queda mientras llega la lista nueva (al buscar no se pierde el foco); con la última lista leída.
    const mostrarBarra = canales !== null && lista !== null && lista.error === null && !(sinCanales && lista.items.length === 0 && !conFiltros) && !(movil && abierta !== null);

    return (
        <div className="rv-bz-conv">
            {mostrarBarra && (
                <div className="rv-bz-herr">
                    <Filtros
                        etiqueta="Estado de las conversaciones"
                        items={filtros.map(([c, etiqueta]) => ({ etiqueta, cifra: c === 'sin-leer' ? (lista.cuentas?.unread ?? null) : null }))}
                        valor={filtroIndice}
                        onCambio={(i) => {
                            const f = filtros[i];
                            if (f !== undefined) {
                                setFiltro(f[0]);
                                setMas(null);
                                if (!movil) abrir(null);
                            }
                        }}
                    />
                    <div className="rv-bz-herr__vista" aria-label="Qué conversaciones ver">
                        <Desplegable etiqueta="Canal" valor={canalId} opciones={opcionesCanal(canales).map((o) => ({ valor: o.valor, texto: o.texto }))} onChange={(v) => { setCanalId(v); setMas(null); }} />
                        <Desplegable etiqueta="Mostrar" valor={asignadas} opciones={opcionesMostrar(quien, lista.cuentas).map((o) => ({ valor: o.valor, texto: o.texto }))} onChange={(v) => { setAsignadas(v as Asignadas); setMas(null); }} />
                        <Campo etiqueta="Buscar por nombre o mensaje" tipo="search" valor={texto} max={100} onChange={setTexto} nombre="buscar-conversacion" ayuda={texto.trim() !== '' && busquedaValida(texto) === null ? 'Escribe al menos 3 letras.' : null} />
                    </div>
                </div>
            )}
            {(quien.suyas || !quien.asistente) && !(movil && abierta !== null) && (
                <p className="rv-bz-nota">
                    <Icono nombre={quien.suyas ? 'eye' : 'info'} />
                    <span>{quien.suyas ? 'Ves las conversaciones asignadas a ti y las que esperan a una persona.' : 'Sin el bloque Asistente IA: todo lo que escriben tus clientes llega aquí para que lo atienda una persona.'}</span>
                </p>
            )}
            {saldo !== null && !(movil && abierta !== null) && <Franja tipo={saldo.tipo} titulo={saldo.titulo} accion={saldo.accion} onAccion={() => { navegar(RUTA_MENSAJES_PLAN); }} movil={movil} espacio={8} />}
            {cuerpo}
            {quien.configura && <AjustesDelBuzon abierto={ajustes} onCerrar={() => { setAjustes(false); }} ops={ops} negocio={negocio} movil={movil} soloLectura={soloLectura} asistente={quien.asistente} />}
        </div>
    );
}

function Fila({ c, activa, ahora, zona, miNombre, miId, onAbrir }: { c: Conversacion; activa: boolean; ahora: Date; zona?: string; miNombre: string | null; miId: string | null; onAbrir: () => void }) {
    const nombre = nombreDe(c);
    const [nombreCanal, icono] = canal(c.channel_type);
    const sinLeer = c.unread_count > 0;
    const extracto = (c.last_message_excerpt ?? '').replace(/\s+/g, ' ').trim() || null;
    return (
        <button type="button" className={`rv-bz-fila${sinLeer ? ' rv-bz-fila--sinleer' : ''}`} aria-current={activa} onClick={onAbrir}>
            <span className={`rv-bz-av${(c.contact.customer_id ?? null) !== null ? ' rv-bz-av--rojo' : ''}`} aria-hidden="true">
                {iniciales(nombre)}
                <span className="rv-bz-av__canal" title={nombreCanal}><Icono nombre={icono} /></span>
            </span>
            <span className="rv-bz-fila__txt">
                <span className="rv-bz-fila__l1"><strong>{nombre}</strong><small>{horaDeLista(c.last_message_at, ahora, zona)}</small></span>
                <span className="rv-bz-fila__l2">
                    {/* G1186: el extracto del último mensaje (también de los automáticos); sin él, el canal. G1206, G1215: quién escribió, delante. */}
                    {extracto === null ? <span>{nombreCanal}</span> : <><span className="rv-sr">{`${nombreCanal}: `}</span><span className="rv-bz-fila__ext">{prefijoDelExtracto(c, miNombre, miId) + extracto}</span></>}
                    {esperaPersona(c) && <Chip estado="pending">Espera</Chip>}
                    {sinLeer && <span className="rv-bz-sinleer" aria-label={`${String(c.unread_count)} sin leer`}>{c.unread_count}</span>}
                </span>
            </span>
        </button>
    );
}
